import { useState, useEffect, useRef } from 'react';
import { listOrgCollection } from '../../storage/FileSystemAdapter';
import CustomerList from './CustomerList';
import CustomerForm from './CustomerForm';
import JurisdictionsView from './JurisdictionsView';
import ConfirmDialog from '../shared/ConfirmDialog';
import { BRAND_PRIMARY, BTN_PRIMARY, BTN_SAVE_AND_LEAVE, BTN_DESTRUCTIVE_OUTLINE, BORDER_DEFAULT, TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, ERROR_BG, ERROR_TEXT, ERROR_BORDER, FONT_PAGE_TITLE, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:       'Gestión de clientes',
    backToList:  '← Volver a la lista',
    clauseRef:   'Cláusula 4.2 · Partes interesadas · ISO/IEC 27001:2022',
    loadError:   'Error al leer los datos. Comprueba el acceso a la carpeta.',
    retry:       'Reintentar',
    newCustomer: '+ Nuevo cliente',
    tabList:           'Lista',
    tabJurisdictions:  'Jurisdicciones',
    unsavedLabel:       'Cambios sin guardar',
    unsavedConfirm:     '¿Salir sin guardar los cambios?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    formCancel:         'Cancelar',
    deleteBtn:          'Eliminar',
  },
  en: {
    title:       'Customer management',
    backToList:  '← Back to list',
    clauseRef:   'Clause 4.2 · Interested parties · ISO/IEC 27001:2022',
    loadError:   'Failed to read data. Check folder access.',
    retry:       'Retry',
    newCustomer: '+ New customer',
    tabList:           'List',
    tabJurisdictions:  'Jurisdictions',
    unsavedLabel:       'Unsaved changes',
    unsavedConfirm:     'Leave without saving changes?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    formCancel:         'Cancel',
    deleteBtn:          'Delete',
  },
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function CustomersView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [customers,   setCustomers]   = useState(null);  // null = loading
  const [formMode,    setFormMode]    = useState(null);  // null | 'create' | 'edit'
  const [editCustomer,setEditCustomer]= useState(null);
  const [loadError,   setLoadError]   = useState(false);
  const [dirty,       setDirty]       = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  const [activeTab,   setActiveTab]   = useState('list');  // 'list' | 'jurisdictions'
  const pendingAppSaveRef             = useRef(false);
  const lastProcessedAppTickRef       = useRef(0);

  useScrollToTopOnFormOpen(formMode !== null);

  const loadCustomers = () => {
    setLoadError(false);
    listOrgCollection(org.id, 'customers')
      .then((items) => {
        const sorted = [...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || ''));
        setCustomers(sorted);
      })
      .catch(() => { setCustomers([]); setLoadError(true); });
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const handleFormSave = (fileId) => {
    if (fileId === null) {
      setFormMode(null);
      setEditCustomer(null);
      setDirty(false);
      setSaveRequestTick(0);
      loadCustomers();
      return;
    }
    setDirty(false);
    setSaveRequestTick(0);
    loadCustomers();
    if (pendingAppSaveRef.current) {
      pendingAppSaveRef.current = false;
      onSaveResult?.(true);
    }
  };

  const closeForm = () => { setFormMode(null); setEditCustomer(null); setDirty(false); };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          color: BTN_SAVE_AND_LEAVE,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── App-level guard: bridge App tick into local tick ─────────────────────
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formMode) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Form view ────────────────────────────────────────────────────────────────
  if (formMode) {
    return (
      <div>
        <button
          onClick={handleFormCancel}
          style={{
            ...FONT_LABEL,
            background: 'none', border: 'none', cursor: 'pointer',
            color: TEXT_MUTED, letterSpacing: '0.03em', padding: 0,
            marginBottom: '0.75rem', display: 'block',
          }}
        >
          {t.backToList}
        </button>
        <CustomerForm
          customer={formMode === 'create' ? null : editCustomer}
          mode={formMode}
          org={org}
          lang={lang}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <h1 style={{ ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0 }}>
            {t.title}
          </h1>
          <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '0.15rem 0 0', letterSpacing: '0.03em' }}>
            {t.clauseRef}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <button
            onClick={() => { setFormMode('create'); setDirty(false); }}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none', borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
            }}
          >
            {t.newCustomer}
          </button>
        </div>
      </div>

      {/* Tab toggle */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER_DEFAULT}`, marginBottom: '1rem' }}>
        <button
          type="button"
          onClick={() => setActiveTab('list')}
          style={{
            ...FONT_BUTTON,
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'list' ? `2px solid ${BRAND_PRIMARY}` : '2px solid transparent',
            color: activeTab === 'list' ? BRAND_PRIMARY : TEXT_MUTED,
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            marginBottom: -1,
          }}
        >
          {t.tabList}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('jurisdictions')}
          style={{
            ...FONT_BUTTON,
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'jurisdictions' ? `2px solid ${BRAND_PRIMARY}` : '2px solid transparent',
            color: activeTab === 'jurisdictions' ? BRAND_PRIMARY : TEXT_MUTED,
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            marginBottom: -1,
          }}
        >
          {t.tabJurisdictions}
        </button>
      </div>

      {/* Load error banner */}
      {loadError && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
          color: ERROR_TEXT,
        }}>
          <span>{t.loadError}</span>
          <button
            onClick={loadCustomers}
            style={{
              ...BTN_DESTRUCTIVE_OUTLINE,
              ...FONT_SMALL,
              borderRadius: 6,
              padding: '0.25rem 0.6rem', cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {t.retry}
          </button>
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'list' ? (
        <CustomerList
          customers={customers}
          loading={customers === null}
          org={org}
          lang={lang}
          onNew={() => { setFormMode('create'); setDirty(false); }}
          onEdit={(c) => { setEditCustomer(c); setFormMode('edit'); setDirty(false); }}
          onView={(c) => { setEditCustomer(c); setFormMode('view'); setDirty(false); }}
          onNavigate={onNavigate}
          onCustomersChanged={loadCustomers}
        />
      ) : (
        <JurisdictionsView
          customers={customers}
          lang={lang}
          org={org}
          onOpenCustomer={(cid) => {
            const target = (customers || []).find((c) => c.id === cid);
            if (target) { setEditCustomer(target); setFormMode('view'); setDirty(false); }
          }}
          onNavigate={onNavigate}
        />
      )}
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
